import _ from 'lodash';
import React from 'react';

import Button from 'components/Button';
import {
  default as DateRangeFilter,
  TopRightFixedCustomOverlay
} from 'components/Filters/DateRangeFilter';
import CustomerFilter from 'components/Filters/CustomerFilter';
import CreatedByAdminOrTeamFilter from 'components/Filters/CreatedByAdminOrTeamFilter';
import TreeSelect from 'components/Filters/TreeSelect';
import SelectFilter from 'components/Filters/SelectFilter';
import BookingFilter from 'components/Filters/BookingFilter';
import ToggleSelectFilter from 'components/Filters/ToggleSelectFilter';

import {fetchCustomerOptions as sdkFetchCustomerOptions} from 'sdk/reporting';
import {
  fetchPaymentsCreatedBy as sdkFetchCreatedBy,
  fetchPaymentsForBooking as sdkFetchBookings
} from 'sdk/payments';

import {
  XERO_STATUSES,
  INVOICE_STATUS_MAPPER_FOR_FILTERS
} from 'ducks/reporting';

import {
  getBookingStatusGroups,
  getBookingStatusesForStatusGroup
} from 'ducks/bookings';

import './styles.css';

const bookingStatusData = getBookingStatusGroups().map(value => ({
  label: value,
  data: getBookingStatusesForStatusGroup(value).map(status => ({
    label: status,
    key: status
  }))
}));

const xeroStatusesData = [
  {label: 'Synced', value: XERO_STATUSES.SYNCED},
  {label: 'Failed', value: XERO_STATUSES.FAILED}
];

interface PassedProps {
  locationRef: string;
  setFilter: (field: string, value: any) => void;
  clearFilters: () => void;
  getFilters: any;
  scheduleTypesFilters: any;
  xeroAccountsFilters: any;
  oneOffChargeReasonFilters: Array<string>;
}

class PaymentListFilters extends React.Component<PassedProps> {
  customerSelectElement: any = null;
  createdBySelectElement: any = null;
  bookingSelectElement: any = null;

  fetchCustomerOptions = search => {
    const {locationRef} = this.props;

    return sdkFetchCustomerOptions({locationRef, search});
  };

  fetchCreatedByOptions = search => {
    const {locationRef} = this.props;

    return sdkFetchCreatedBy({locationRef, search});
  };

  fetchBookingOptions = search => {
    const {locationRef} = this.props;

    return sdkFetchBookings({locationRef, search});
  };

  clearFilters = () => {
    if (_.get(this.customerSelectElement, 'state.selectedOption')) {
      this.customerSelectElement.clearSelectedOption();
    }

    if (_.get(this.createdBySelectElement, 'state.selectedOption')) {
      this.createdBySelectElement.clearSelectedOption();
    }

    if (_.get(this.bookingSelectElement, 'state.selectedOption')) {
      this.bookingSelectElement.clearSelectedOption();
    }

    this.props.clearFilters();
  };

  onChangeCreatedBy = selectedOption => {
    const {setFilter} = this.props;

    if (_.isNull(selectedOption)) {
      setFilter('createdBy', null);
      setFilter('createdByTeam', null);
    } else if (selectedOption.object.isTeam) {
      setFilter('createdBy', null);
      setFilter('createdByTeam', selectedOption.value);
    } else {
      setFilter('createdBy', selectedOption.value);
      setFilter('createdByTeam', null);
    }
  };

  render() {
    const {
      setFilter,
      scheduleTypesFilters,
      xeroAccountsFilters,
      oneOffChargeReasonFilters
    } = this.props;
    const filters: any = this.props.getFilters();

    const oneOffChargeReasonOptions = oneOffChargeReasonFilters.map(reason => ({
      label: reason,
      value: reason
    }));

    return (
      <div className="PaymentsListFilters colab-row-space-between">
        <div className="colab-sidepadding-2">
          <CustomerFilter
            customerRef={el => (this.customerSelectElement = el)}
            fetchFunction={this.fetchCustomerOptions}
            clearOption={() => setFilter('customer', null)}
            onChange={customer => setFilter('customer', customer)}
          />
          <BookingFilter
            fetchSdk={this.fetchBookingOptions}
            bookingSelectRef={el => (this.bookingSelectElement = el)}
            onChange={this.props.setFilter}
            value={filters.booking}
          />
          <TreeSelect
            label="Booking Status"
            classNameForTesting="bookingStatus"
            data={bookingStatusData}
            value={filters.bookingStatuses}
            onFilterChange={bookingStatuses => {
              setFilter('bookingStatuses', bookingStatuses);
            }}
          />
          <ToggleSelectFilter
            label="Xero Sync Status"
            classNameForTesting="xeroSyncStatus"
            value={filters.xeroStatuses}
            data={xeroStatusesData}
            onChange={xeroStatuses => setFilter('xeroStatuses', xeroStatuses)}
          />
          <SelectFilter
            label="Schedule Type"
            classNameForTesting="scheduleType"
            data={scheduleTypesFilters}
            value={filters.scheduleTypes}
            onChange={scheduleTypes =>
              setFilter('scheduleTypes', scheduleTypes)
            }
          />
          <SelectFilter
            label="Xero Accounts"
            classNameForTesting="xeroAccounts"
            data={xeroAccountsFilters}
            value={filters.xeroAccountsOptions}
            onChange={xeroAccountsOptions =>
              setFilter('xeroAccountsOptions', xeroAccountsOptions)
            }
          />
          <SelectFilter
            label="One Off Charge Reason"
            classNameForTesting="oneOffChargeReason"
            data={oneOffChargeReasonOptions}
            value={filters.oneOffChargeReasons}
            onChange={oneOffChargeReasons =>
              setFilter('oneOffChargeReasons', oneOffChargeReasons)
            }
          />
          <CreatedByAdminOrTeamFilter
            createdByRef={el => (this.createdBySelectElement = el)}
            fetchFunction={this.fetchCreatedByOptions}
            clearOption={() => setFilter('createdBy', null)}
            onChange={this.onChangeCreatedBy}
          />
          <DateRangeFilter
            label="Created At Date"
            fieldName="createdAtDate"
            classNameForTesting="createdDate"
            start={filters.createdAtAfter}
            end={filters.createdAtBefore}
            startFieldName="createdAtAfter"
            endFieldName="createdAtBefore"
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <DateRangeFilter
            label="Payment Date"
            fieldName="paymentDate"
            classNameForTesting="paymentDate"
            start={filters.paymentDateAfter}
            end={filters.paymentDateBefore}
            startFieldName="paymentDateAfter"
            endFieldName="paymentDateBefore"
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <ToggleSelectFilter
            label="Invoice Status"
            classNameForTesting="invoiceStatus"
            value={filters.invoiceStatuses}
            data={INVOICE_STATUS_MAPPER_FOR_FILTERS}
            onChange={invoiceStatuses =>
              setFilter('invoiceStatuses', invoiceStatuses)
            }
          />
          <DateRangeFilter
            label="Invoice Due Date"
            fieldName="invoiceDueDate"
            classNameForTesting="invoiceDueDate"
            start={filters.invoiceDueDateAfter}
            end={filters.invoiceDueDateBefore}
            startFieldName="invoiceDueDateAfter"
            endFieldName="invoiceDueDateBefore"
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
        </div>
        <div>
          <Button clear onClick={this.clearFilters}>
            Clear all filters
          </Button>
        </div>
      </div>
    );
  }
}

export default PaymentListFilters;
